import type { Emotion } from '../types/emotion';
import type { PlayPose } from './heroPlay';

/**
 * Idle antics — when the visitor goes quiet for a while, Pixel gets bored and entertains himself.
 * After ~7s without any input he starts a random antic, pauses, does another… and after a few he
 * finally dozes off. Any input ends it at once ("caught in the act" — a quick startled look).
 *
 *  yawn       Huge yawn, side plates stretched wide, leans back… then a little shake-off wiggle
 *  beatbox    Hums a tune: nods to the beat, sways, LED ring pulsing in rhythm
 *  bug        Spots an imaginary fly, tracks its loops, snaps at it — misses — snaps again, got it!
 *  peekaboo   Turns his back to "hide"… then whips round: BOO!
 *  knock      Floats right up to the glass, knocks three times — "hello? anyone in there?"
 *  twirl      Show-off pirouette, gets dizzy, wobbles, plays it cool
 *  nodoff     Nods off mid-air, jolts awake, glances around to check nobody saw
 */

type AnticName = 'yawn' | 'beatbox' | 'bug' | 'peekaboo' | 'knock' | 'twirl' | 'nodoff';

const ANTICS: Record<AnticName, number> = {
  yawn: 4.6,
  beatbox: 6,
  bug: 6.4,
  peekaboo: 4.4,
  knock: 5.4,
  twirl: 4.6,
  nodoff: 5.6,
};

/** Quiet this long (s) before the first antic */
const IDLE_START = 7;
/** Antics per session before he finally falls asleep */
const PER_SESSION = 4;
const CAUGHT_DUR = 0.8;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const smooth = (v: number) => {
  const c = clamp01(v);
  return c * c * (3 - 2 * c);
};
/** 0 → 1 → 0 window between a and b with soft edges */
const bump = (v: number, a: number, b: number, edge = 0.25) => smooth((v - a) / edge) * smooth((b - v) / edge);
/** Short pulse (0..1..0) of length `len` starting at `at` */
const pulse = (v: number, at: number, len: number) => (v > at && v < at + len ? Math.sin(((v - at) / len) * Math.PI) : 0);

function base(rate = 3.5): PlayPose {
  return {
    mode: 'none',
    x: 0, y: 0, z: 0, pitch: 0, yaw: 0, roll: 0, squint: 0, led: 0, board: 0,
    wide: 0, spiral: 0, tight: 0, scan: 0,
    heading: 0, spin: 0, arms: null, emotion: null, gaze: null, headFollow: 0.5, rate, push: false,
  };
}

export class IdleAntics {
  private lastInput = performance.now() / 1000;
  private current: AnticName | null = null;
  private startedAt = 0;
  private nextAt = 0;
  private done = 0;
  private last: AnticName | null = null;
  private caughtAt = -10;
  private listening = false;

  /** While a session of antics runs, the brain shouldn't fall asleep */
  holdAwake = false;

  private onInput = () => {
    const now = performance.now() / 1000;
    if (this.current) this.caughtAt = now; // caught in the act!
    this.current = null;
    this.done = 0;
    this.holdAwake = false;
    this.lastInput = now;
  };

  attach() {
    if (this.listening) return;
    this.listening = true;
    const opts = { passive: true } as AddEventListenerOptions;
    ['pointermove', 'pointerdown', 'wheel', 'keydown', 'touchstart', 'scroll'].forEach((ev) =>
      window.addEventListener(ev, this.onInput, opts),
    );
  }

  detach() {
    if (!this.listening) return;
    this.listening = false;
    ['pointermove', 'pointerdown', 'wheel', 'keydown', 'touchstart', 'scroll'].forEach((ev) =>
      window.removeEventListener(ev, this.onInput),
    );
  }

  private pick(): AnticName {
    const names = (Object.keys(ANTICS) as AnticName[]).filter((n) => n !== this.last);
    return names[Math.floor(Math.random() * names.length)];
  }

  update(t: number, enabled: boolean): PlayPose | null {
    const now = performance.now() / 1000;
    if (!enabled) {
      this.current = null;
      this.done = 0;
      this.holdAwake = false;
      this.lastInput = Math.max(this.lastInput, now - IDLE_START + 2);
      return null;
    }

    // Caught in the act: a quick startled "wasn't me" look
    if (now - this.caughtAt < CAUGHT_DUR) {
      const p = base(8);
      p.emotion = 'surprised';
      p.z = 0.15 * pulse(now - this.caughtAt, 0, CAUGHT_DUR);
      p.gaze = { x: 0, y: 0 };
      p.headFollow = 0.3;
      p.led = 0.6;
      return p;
    }

    const idle = now - this.lastInput;
    if (idle < IDLE_START || this.done >= PER_SESSION) {
      this.holdAwake = idle >= IDLE_START && this.done < PER_SESSION;
      return null;
    }
    this.holdAwake = true;

    if (!this.current) {
      if (this.nextAt === 0 || this.done === 0) this.nextAt = Math.min(this.nextAt || now, now);
      if (now < this.nextAt) return null;
      this.current = this.pick();
      this.last = this.current;
      this.startedAt = now;
    }

    const name = this.current;
    const at = now - this.startedAt;
    const dur = ANTICS[name];
    if (at >= dur) {
      this.current = null;
      this.done += 1;
      this.nextAt = now + 3.5 + Math.random() * 2.5;
      // Last one done → he finally drifts off to sleep
      if (this.done >= PER_SESSION) this.holdAwake = false;
      return null;
    }
    // Every antic eases in and out of its pose
    const env = smooth(at / 0.5) * smooth((dur - at) / 0.6);
    const pose = this.pose(name, at, t, dur);
    for (const k of ['x', 'y', 'z', 'pitch', 'yaw', 'roll', 'squint', 'led', 'wide', 'spiral', 'scan'] as const) {
      // A finished pirouette is a full turn — never unwind it
      if (k === 'yaw' && name === 'twirl') continue;
      pose[k] *= env;
    }
    pose.mode = 'none';
    return pose;
  }

  private pose(name: AnticName, a: number, t: number, dur: number): PlayPose {
    const p = base();
    let emotion: Emotion = 'neutral';

    switch (name) {
      case 'yawn': {
        // 0.3–2.2 yawn: rises, leans back, mouth wide, eyes squeezed, plates stretch out
        const yawn = bump(a, 0.3, 2.3, 0.6);
        p.y = 0.12 * yawn;
        p.pitch = -0.32 * yawn;
        p.roll = 0.08 * yawn;
        p.squint = 0.9 * yawn;
        p.arms = yawn > 0.3 ? -0.08 : 0;
        emotion = yawn > 0.2 ? 'surprised' : 'sleepy';
        // 2.6–3.6 shake it off: quick wiggle, plates clack
        const shake = bump(a, 2.6, 3.6, 0.15);
        p.roll += Math.sin(a * 32) * 0.14 * shake;
        p.yaw = Math.sin(a * 26) * 0.08 * shake;
        if (shake > 0.2) p.arms = 0.5 + 0.5 * Math.sin(a * 32);
        if (a > 3.6) emotion = 'happy';
        p.gaze = { x: 0, y: 0.2 * yawn };
        p.headFollow = 0.2;
        break;
      }
      case 'beatbox': {
        const bpm = 112 / 60;
        const beat = (a * bpm) % 1;
        const hit = Math.exp(-beat * 9);
        p.pitch = 0.12 * hit;
        p.y = -0.04 * hit + Math.sin(a * bpm * Math.PI) * 0.03;
        p.x = Math.sin(a * bpm * Math.PI) * 0.12;
        p.roll = Math.sin(a * bpm * Math.PI) * 0.12;
        p.yaw = Math.sin(a * bpm * Math.PI * 0.5) * 0.15;
        p.led = 0.9 * hit;
        p.arms = 0.25 + 0.35 * hit;
        p.squint = 0.25;
        emotion = 'happy';
        p.gaze = { x: Math.sin(a * bpm * Math.PI * 0.5) * 0.4, y: 0.1 };
        p.headFollow = 0.3;
        break;
      }
      case 'bug': {
        // An imaginary fly loops a figure-eight in front of him
        const fx = Math.sin(a * 2.1) * 0.85;
        const fy = Math.sin(a * 4.2) * 0.45 + 0.15;
        p.gaze = { x: fx, y: fy };
        p.headFollow = 0.75;
        p.wide = 0.8;
        emotion = 'curious';
        // Snap #1 (miss) at 2.4s, snap #2 (got it) at 4.4s
        const snap1 = pulse(a, 2.4, 0.35);
        const snap2 = pulse(a, 4.4, 0.35);
        const snap = Math.max(snap1, snap2);
        p.x = fx * 0.25 * snap;
        p.y = fy * 0.2 * snap;
        p.z = 0.55 * snap;
        p.arms = snap > 0.2 ? 1 : 0.1;
        if (a > 2.75 && a < 3.6) {
          emotion = 'sad'; // missed…
          p.roll = Math.sin(a * 3) * 0.12;
        }
        if (a > 4.75) {
          emotion = 'happy'; // got it!
          p.wide = 0;
          p.y += 0.06 * Math.abs(Math.sin(a * 9));
          p.gaze = { x: 0, y: -0.2 };
        }
        p.led = snap * 1.2;
        break;
      }
      case 'peekaboo': {
        // Turns his back to "hide", peeks once, then whips round: BOO!
        const hide = smooth((a - 0.3) / 0.7) * (1 - smooth((a - 3.0) / 0.22));
        const peek = pulse(a, 1.7, 0.6);
        p.yaw = Math.PI * hide - peek * 0.5;
        p.z = -0.2 * hide;
        p.y = -0.05 * hide;
        const boo = pulse(a, 3.0, 0.6);
        p.z += 0.7 * boo;
        p.y += 0.08 * boo;
        p.arms = hide > 0.5 ? 1 : boo > 0.1 ? -0.08 : 0;
        p.led = boo * 1.6;
        emotion = a > 3.0 ? (a > 3.6 ? 'happy' : 'surprised') : 'happy';
        p.gaze = { x: 0, y: 0 };
        p.headFollow = 0.2;
        p.rate = 7;
        break;
      }
      case 'knock': {
        // Up to the glass, three knocks, "hello?", back off
        const close = smooth((a - 0.2) / 0.9) * smooth((dur - 0.4 - a) / 0.9);
        p.z = 1.9 * close;
        p.y = 0.05 * close;
        const k = pulse(a, 1.4, 0.2) + pulse(a, 1.75, 0.2) + pulse(a, 2.1, 0.2);
        p.z += 0.22 * k;
        p.pitch = -0.08 * k;
        p.led = 1.1 * k;
        // Listens… head tilt left, then right
        p.roll = bump(a, 2.6, 3.4, 0.2) * 0.25 - bump(a, 3.5, 4.3, 0.2) * 0.25;
        p.wide = bump(a, 2.5, 4.4, 0.3) * 0.9;
        emotion = a > 2.5 ? 'curious' : 'neutral';
        p.gaze = { x: Math.sin(a * 1.4) * 0.3, y: 0 };
        p.headFollow = 0.3;
        p.rate = 6;
        break;
      }
      case 'twirl': {
        // Pirouette (one full turn), dizzy wobble, then plays it cool
        const spin = smooth((a - 0.4) / 1.3);
        p.yaw = spin * Math.PI * 2;
        p.y = 0.1 * bump(a, 0.4, 1.7, 0.4);
        p.arms = a > 0.4 && a < 1.7 ? 1 : 0;
        const dizzy = bump(a, 1.7, 3.4, 0.3);
        p.roll = Math.sin(a * 6) * 0.2 * dizzy;
        p.x = Math.sin(a * 3.5) * 0.08 * dizzy;
        p.spiral = dizzy;
        emotion = a < 1.7 ? 'happy' : dizzy > 0.3 ? 'surprised' : 'happy';
        p.squint = a > 3.4 ? 0.3 : 0; // smug
        p.gaze = { x: 0, y: 0 };
        p.headFollow = 0.2;
        p.rate = 7;
        break;
      }
      case 'nodoff': {
        // Eyes droop, head sinks… sinks… JOLT awake, look left, right, act natural
        const droop = smooth(a / 2.4) * (1 - smooth((a - 2.6) / 0.12));
        p.pitch = 0.35 * droop;
        p.y = -0.16 * droop;
        p.squint = droop;
        p.roll = 0.06 * droop;
        const jolt = pulse(a, 2.6, 0.35);
        p.y += 0.12 * jolt;
        p.z = 0.2 * jolt;
        p.arms = jolt > 0.2 ? 1 : droop > 0.5 ? 0.4 : 0;
        emotion = a < 2.6 ? 'sleepy' : a < 3.1 ? 'surprised' : 'neutral';
        // Glance around: left… right… back to you
        const look = a < 3.1 ? 0 : a < 3.8 ? -0.8 : a < 4.5 ? 0.8 : 0;
        p.gaze = { x: look, y: 0 };
        p.headFollow = 0.7;
        p.wide = a > 3.1 ? 0.7 : 0;
        p.led = jolt * 1.4 - droop * 0.6;
        p.rate = 6;
        break;
      }
    }

    p.emotion = emotion;
    p.spin = t * 4; // spiral eyes keep turning (twirl dizziness)
    return p;
  }
}

export const idleAntics = new IdleAntics();
